import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import type { Integration, PipelinesConnection } from '@/services/db/integration/entity';
import { getIntegrationsQueryOptions, getPipelinesConnectionsQueryOptions } from '@/services/db/integration/queries';
import { useIntegrationsWorkspaceId } from '../workspace-id';
import { buildDeveloperConnectionsList, buildPipelinesConnectionsById } from '../connections/view-model/build';
import type { IntegrationConnection } from '../connections/view-model/types';

const EMPTY_PIPELINES: readonly PipelinesConnection[] = [];
const EMPTY_CATALOG: readonly Integration[] = [];

/**
 * The Developers sub-tab view model: the pipelines connections joined against
 * the catalog and restricted to `Developer`-type integrations. Unlike the
 * Connections tab, no Journeys-side sources are consulted — the list is
 * pipelines-only.
 *
 * Both reads are non-suspense queries that degrade to an empty default on
 * error, so a failing source renders the blank slate rather than throwing to
 * the app error boundary — matching the Connections tab. The pipelines read
 * keeps `staleTime: 0`, so re-entering the route (e.g. returning from the
 * Pipelines app) refetches on mount.
 */
export function useDeveloperConnectionsViewModel(): {
  connections: IntegrationConnection[];
  /**
   * Every fetched pipelines connection by wire id, for the Connections column
   * lookup. Not restricted to `Developer` connections: Ember peeks the whole store,
   * so a developer source's related destination resolves even though it never
   * appears in this list.
   */
  pipelinesConnectionsById: ReadonlyMap<string, IntegrationConnection>;
  isLoading: boolean;
  shouldGroupAutoTrack: boolean;
} {
  const workspaceId = useIntegrationsWorkspaceId();
  const queryClient = useQueryClient();
  const pipelines = useQuery(getPipelinesConnectionsQueryOptions(workspaceId, queryClient));
  const catalog = useQuery(getIntegrationsQueryOptions(workspaceId, queryClient));

  const trackEnabled = useFeatureFlag(AccountFeatureFlag.CdpTrackEnabled);
  const passthroughEnabled = useFeatureFlag(AccountFeatureFlag.CdpPassthroughEnabled);
  const areAutoTrackSourcesEnabled = trackEnabled || passthroughEnabled;

  const pipelinesData = pipelines.data ?? EMPTY_PIPELINES;
  const catalogData = catalog.data ?? EMPTY_CATALOG;

  const connections = useMemo(
    () =>
      buildDeveloperConnectionsList({
        pipelinesConnections: pipelinesData,
        catalogIntegrations: catalogData,
        areAutoTrackSourcesEnabled,
      }),
    [pipelinesData, catalogData, areAutoTrackSourcesEnabled],
  );

  const pipelinesConnectionsById = useMemo(
    () => buildPipelinesConnectionsById(pipelinesData, catalogData),
    [pipelinesData, catalogData],
  );

  return {
    connections,
    pipelinesConnectionsById,
    isLoading: pipelines.isLoading || catalog.isLoading,
    shouldGroupAutoTrack: areAutoTrackSourcesEnabled,
  };
}
